/**
 * Backlog Health — Zombie Priority Cleanup Tool
 *
 * Detects and surfaces zombie priorities: active items with no recent activity
 * or cycle participation that are quietly consuming mental space without moving.
 *
 * Two categories:
 *   1. Zombie Priorities — exit_status = 'active', no cycle participation, stale
 *   2. Overdue Deferrals — exit_status = 'deferred', defer_review_date in the past
 *
 * Per-item actions: Archive, Defer (with review date), Keep Active
 * Bulk actions: Archive selected, Defer selected
 * Notify toggle: sends item_deprioritized notification to item steward
 */

import { useState, useMemo, useCallback } from 'react';
import {
  AlertTriangle,
  Archive,
  Clock,
  RefreshCw,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Filter,
  Skull,
  CalendarClock,
  Undo2,
  Loader2,
  Sparkles,
  X,
} from 'lucide-react';
import { generateBacklogHealthNarrative } from '../../../lib/llmBacklogHealthService';
import { useAiCapability } from '../../../lib/ai/useAiCapability';
import type { BacklogHealthNarrative } from '../../../lib/llmBacklogHealthService';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useItemPoolsStore } from '../../../stores/prioritization/itemPoolsStore';
import { useSessionStore } from '../../../stores/prioritization/sessionStore';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import type { Item } from '../../../types/prioritization/lifecycle';
import type { ItemExitStatus } from '../../../types/prioritization/itemLifecycle';

// ============================================================================
// Types
// ============================================================================

type StalenessThreshold = 30 | 60 | 90 | 180;

interface ZombieItem extends Item {
  cycleIds: string[];
  daysSinceUpdate: number;
}

interface OverdueDeferral extends Item {
  deferReviewDate: Date;
  daysOverdue: number;
}

interface InlineDefer {
  itemId: string;
  date: string;
}

// ============================================================================
// Helpers
// ============================================================================

function daysSince(date: Date | string): number {
  const ms = date instanceof Date ? date.getTime() : new Date(date).getTime();
  return Math.floor((Date.now() - ms) / (1000 * 60 * 60 * 24));
}

function formatDate(date: Date | string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date instanceof Date ? date : new Date(date));
}

function todayPlusDays(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
}

// ============================================================================
// Sub-components
// ============================================================================

interface ZombieRowProps {
  item: ZombieItem;
  selected: boolean;
  onToggle: () => void;
  onArchive: () => void;
  onDefer: (reviewDate: Date) => void;
  onKeepActive: () => void;
  inlineDefer: InlineDefer | null;
  setInlineDefer: (d: InlineDefer | null) => void;
  loading: boolean;
}

function ZombieRow({
  item,
  selected,
  onToggle,
  onArchive,
  onDefer,
  onKeepActive,
  inlineDefer,
  setInlineDefer,
  loading,
}: ZombieRowProps) {
  const isDeferring = inlineDefer?.itemId === item.id;

  return (
    <div className="flex items-start gap-3 border border-[var(--rule)] bg-[var(--bg-raised)] p-4 hover:border-[var(--rule)]">
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        disabled={loading}
        className="mt-1 h-4 w-4 rounded border-[var(--rule)] text-primary-600 focus:ring-primary-500"
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-[var(--text-pri)] truncate">{item.title}</span>
          {item.cycleIds.length === 0 ? (
            <span className="inline-flex items-center rounded-full bg-[var(--hover-tint)] px-2 py-0.5 text-xs text-[var(--text-ter)]">
              Never in a cycle
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-[var(--mustard-dim)] px-2 py-0.5 text-xs text-[var(--mustard)]">
              {item.cycleIds.length} past cycle{item.cycleIds.length > 1 ? 's' : ''}
            </span>
          )}
          {item.itemCategory && (
            <span className="inline-flex items-center rounded-full bg-[var(--slate-dim)] px-2 py-0.5 text-xs text-[var(--text-sec)] capitalize">
              {item.itemCategory}
            </span>
          )}
        </div>
        {item.description && (
          <p className="mt-1 text-xs text-[var(--text-ter)] line-clamp-2">{item.description}</p>
        )}
        <p className="mt-1 text-xs text-[var(--text-ter)]">
          Last updated {item.daysSinceUpdate} days ago
        </p>

        {isDeferring && (
          <div className="mt-3 flex items-center gap-2">
            <label className="text-xs text-[var(--text-sec)]">Review date:</label>
            <input
              type="date"
              min={todayPlusDays(1)}
              value={inlineDefer.date}
              onChange={(e) => setInlineDefer({ itemId: item.id, date: e.target.value })}
              className="rounded border border-[var(--rule)] px-2 py-1 text-xs focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <button
              onClick={() => {
                if (inlineDefer.date) onDefer(new Date(inlineDefer.date));
              }}
              disabled={!inlineDefer.date || loading}
              className="rounded bg-[var(--mustard)] px-2 py-1 text-xs font-medium text-white hover:bg-[var(--mustard)] disabled:opacity-40"
            >
              Confirm Defer
            </button>
            <button
              onClick={() => setInlineDefer(null)}
              className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
      {!isDeferring && (
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => setInlineDefer({ itemId: item.id, date: todayPlusDays(30) })}
            disabled={loading}
            title="Defer with review date"
            className="rounded px-2 py-1 text-xs text-[var(--mustard)] hover:bg-[var(--mustard-dim)] disabled:opacity-40 border border-[var(--mustard)]"
          >
            Defer
          </button>
          <button
            onClick={onArchive}
            disabled={loading}
            title="Archive this item"
            className="rounded px-2 py-1 text-xs text-[var(--coral)] hover:bg-[var(--coral-dim)] disabled:opacity-40 border border-[var(--coral)]"
          >
            Archive
          </button>
          <button
            onClick={onKeepActive}
            disabled={loading}
            title="Mark as reviewed — keep active"
            className="rounded px-2 py-1 text-xs text-[var(--text-sec)] hover:bg-[var(--hover-tint)] disabled:opacity-40 border border-[var(--rule)]"
          >
            Keep
          </button>
        </div>
      )}
      {loading && <Loader2 className="h-4 w-4 animate-spin text-[var(--text-ter)] flex-shrink-0" />}
    </div>
  );
}

interface OverdueDeferralRowProps {
  item: OverdueDeferral;
  selected: boolean;
  onToggle: () => void;
  onArchive: () => void;
  onRedefer: (reviewDate: Date) => void;
  onReactivate: () => void;
  inlineDefer: InlineDefer | null;
  setInlineDefer: (d: InlineDefer | null) => void;
  loading: boolean;
}

function OverdueDeferralRow({
  item,
  selected,
  onToggle,
  onArchive,
  onRedefer,
  onReactivate,
  inlineDefer,
  setInlineDefer,
  loading,
}: OverdueDeferralRowProps) {
  const isRedeferring = inlineDefer?.itemId === item.id;

  return (
    <div className="flex items-start gap-3 border border-[var(--mustard)] bg-[var(--mustard-dim)]/40 p-4 hover:border-[var(--mustard)]">
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        disabled={loading}
        className="mt-1 h-4 w-4 rounded border-[var(--rule)] text-primary-600 focus:ring-primary-500"
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-[var(--text-pri)] truncate">{item.title}</span>
          <span className="inline-flex items-center rounded-full bg-[var(--mustard-dim)] px-2 py-0.5 text-xs text-[var(--mustard)]">
            Review overdue by {item.daysOverdue} day{item.daysOverdue !== 1 ? 's' : ''}
          </span>
          {item.itemCategory && (
            <span className="inline-flex items-center rounded-full bg-[var(--slate-dim)] px-2 py-0.5 text-xs text-[var(--text-sec)] capitalize">
              {item.itemCategory}
            </span>
          )}
        </div>
        {item.description && (
          <p className="mt-1 text-xs text-[var(--text-ter)] line-clamp-2">{item.description}</p>
        )}
        <p className="mt-1 text-xs text-[var(--text-ter)]">
          Was due for review {formatDate(item.deferReviewDate)}
        </p>

        {isRedeferring && (
          <div className="mt-3 flex items-center gap-2">
            <label className="text-xs text-[var(--text-sec)]">New review date:</label>
            <input
              type="date"
              min={todayPlusDays(1)}
              value={inlineDefer.date}
              onChange={(e) => setInlineDefer({ itemId: item.id, date: e.target.value })}
              className="rounded border border-[var(--rule)] px-2 py-1 text-xs focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <button
              onClick={() => {
                if (inlineDefer.date) onRedefer(new Date(inlineDefer.date));
              }}
              disabled={!inlineDefer.date || loading}
              className="rounded bg-[var(--mustard)] px-2 py-1 text-xs font-medium text-white hover:bg-[var(--mustard)] disabled:opacity-40"
            >
              Update Deferral
            </button>
            <button
              onClick={() => setInlineDefer(null)}
              className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
      {!isRedeferring && (
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => setInlineDefer({ itemId: item.id, date: todayPlusDays(30) })}
            disabled={loading}
            title="Set a new review date"
            className="rounded px-2 py-1 text-xs text-[var(--mustard)] hover:bg-[var(--mustard-dim)] disabled:opacity-40 border border-[var(--mustard)]"
          >
            Re-defer
          </button>
          <button
            onClick={onArchive}
            disabled={loading}
            title="Archive this item"
            className="rounded px-2 py-1 text-xs text-[var(--coral)] hover:bg-[var(--coral-dim)] disabled:opacity-40 border border-[var(--coral)]"
          >
            Archive
          </button>
          <button
            onClick={onReactivate}
            disabled={loading}
            title="Return to active consideration"
            className="rounded px-2 py-1 text-xs text-[var(--green)] dark:text-[var(--text-ter)] hover:bg-[var(--green-dim)] disabled:opacity-40 border border-[var(--green)]"
          >
            Reactivate
          </button>
        </div>
      )}
      {loading && <Loader2 className="h-4 w-4 animate-spin text-[var(--text-ter)] flex-shrink-0" />}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function BacklogHealth() {
  const backlogHealthNarratorEnabled = useAiCapability('backlog_health_narrator');
  const items = useItemsStore((s) => s.items);
  const updateItem = useItemsStore((s) => s.updateItem);
  const itemPools = useItemPoolsStore((s) => s.itemPools);
  const sessions = useSessionStore((s) => s.sessions);

  // ── Filter state ────────────────────────────────────────────────────────────
  const [stalenessThreshold, setStalenessThreshold] = useState<StalenessThreshold>(90);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  // ── Selection state ─────────────────────────────────────────────────────────
  const [selectedZombieIds, setSelectedZombieIds] = useState<Set<string>>(new Set());
  const [selectedOverdueIds, setSelectedOverdueIds] = useState<Set<string>>(new Set());

  // ── Inline defer state ──────────────────────────────────────────────────────
  const [inlineDefer, setInlineDefer] = useState<InlineDefer | null>(null);

  // ── Loading state per item ──────────────────────────────────────────────────
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  // ── Dismissed items (kept active this session) ──────────────────────────────
  const [keptActiveIds, setKeptActiveIds] = useState<Set<string>>(new Set());

  // ── Sections collapsed ──────────────────────────────────────────────────────
  const [zombieCollapsed, setZombieCollapsed] = useState(false);
  const [overdueCollapsed, setOverdueCollapsed] = useState(false);

  // ── AI health narrative ──────────────────────────────────────────────────────
  const [healthNarrative, setHealthNarrative] = useState<BacklogHealthNarrative | null>(null);
  const [isNarrating, setIsNarrating] = useState(false);

  // ── Build cycle membership map ──────────────────────────────────────────────
  const itemToCycleIds = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const pool of itemPools ?? []) {
      for (const itemId of pool.priorityIds ?? []) {
        if (pool.cycleId) {
          if (!map.has(itemId)) map.set(itemId, new Set());
          map.get(itemId)!.add(pool.cycleId);
        }
      }
    }
    for (const session of sessions ?? []) {
      for (const itemId of session.itemIds ?? []) {
        if (session.cycleId) {
          if (!map.has(itemId)) map.set(itemId, new Set());
          map.get(itemId)!.add(session.cycleId);
        }
      }
    }
    return map;
  }, [itemPools, sessions]);

  // ── Detect zombie items ─────────────────────────────────────────────────────
  const zombieItems = useMemo<ZombieItem[]>(() => {
    const thresholdMs = stalenessThreshold * 24 * 60 * 60 * 1000;
    return items
      .filter((item) => {
        if ((item.exit_status ?? 'active') !== 'active') return false;
        if (keptActiveIds.has(item.id)) return false;
        const updatedMs =
          item.updatedAt instanceof Date
            ? item.updatedAt.getTime()
            : new Date(item.updatedAt).getTime();
        if (Date.now() - updatedMs < thresholdMs) return false;
        if (categoryFilter !== 'all' && item.itemCategory !== categoryFilter) return false;
        return true;
      })
      .map((item) => ({
        ...item,
        cycleIds: Array.from(itemToCycleIds.get(item.id) ?? []),
        daysSinceUpdate: daysSince(item.updatedAt),
      }))
      .sort((a, b) => b.daysSinceUpdate - a.daysSinceUpdate);
  }, [items, stalenessThreshold, categoryFilter, itemToCycleIds, keptActiveIds]);

  // ── Detect overdue deferrals ────────────────────────────────────────────────
  const overdueDeferrals = useMemo<OverdueDeferral[]>(() => {
    const now = new Date();
    return items
      .filter((item) => {
        if ((item.exit_status ?? 'active') !== 'deferred') return false;
        if (keptActiveIds.has(item.id)) return false;
        if (!item.deferReviewDate) return false;
        const rd = item.deferReviewDate instanceof Date ? item.deferReviewDate : new Date(item.deferReviewDate);
        if (rd > now) return false;
        if (categoryFilter !== 'all' && item.itemCategory !== categoryFilter) return false;
        return true;
      })
      .map((item) => {
        const rd = item.deferReviewDate instanceof Date ? item.deferReviewDate : new Date(item.deferReviewDate!);
        return {
          ...item,
          deferReviewDate: rd,
          daysOverdue: daysSince(rd),
        };
      })
      .sort((a, b) => b.daysOverdue - a.daysOverdue);
  }, [items, categoryFilter, keptActiveIds]);

  // ── Category options ────────────────────────────────────────────────────────
  const categoryOptions = useMemo(() => {
    const cats = new Set<string>();
    for (const item of [...zombieItems, ...overdueDeferrals]) {
      if (item.itemCategory) cats.add(item.itemCategory);
    }
    return Array.from(cats).sort();
  }, [zombieItems, overdueDeferrals]);

  // ── Helpers ─────────────────────────────────────────────────────────────────

  function markLoading(id: string, on: boolean) {
    setLoadingIds((prev) => {
      const next = new Set(prev);
      if (on) { next.add(id); } else { next.delete(id); }
      return next;
    });
  }

  // ── Per-item actions ────────────────────────────────────────────────────────

  const archiveItem = useCallback(
    async (item: Item) => {
      markLoading(item.id, true);
      try {
        updateItem(item.id, { exit_status: 'archived' as ItemExitStatus });
        setSelectedZombieIds((s) => { const n = new Set(s); n.delete(item.id); return n; });
        setSelectedOverdueIds((s) => { const n = new Set(s); n.delete(item.id); return n; });
      } finally {
        markLoading(item.id, false);
      }
    },
    [updateItem]
  );

  const deferItem = useCallback(
    async (item: Item, reviewDate: Date) => {
      markLoading(item.id, true);
      setInlineDefer(null);
      try {
        updateItem(item.id, {
          exit_status: 'deferred' as ItemExitStatus,
          deferReviewDate: reviewDate,
        });
        setSelectedZombieIds((s) => { const n = new Set(s); n.delete(item.id); return n; });
        setSelectedOverdueIds((s) => { const n = new Set(s); n.delete(item.id); return n; });
      } finally {
        markLoading(item.id, false);
      }
    },
    [updateItem]
  );

  const keepActive = useCallback((item: Item) => {
    setKeptActiveIds((s) => new Set(s).add(item.id));
    setSelectedZombieIds((s) => { const n = new Set(s); n.delete(item.id); return n; });
  }, []);

  const reactivateItem = useCallback(
    (item: Item) => {
      updateItem(item.id, { exit_status: 'active' as ItemExitStatus });
      setKeptActiveIds((s) => new Set(s).add(item.id));
      setSelectedOverdueIds((s) => { const n = new Set(s); n.delete(item.id); return n; });
    },
    [updateItem]
  );

  // ── Bulk actions ────────────────────────────────────────────────────────────

  const bulkArchive = useCallback(
    async (ids: Set<string>, sourceItems: Item[]) => {
      if (ids.size === 0) return;
      setBulkLoading(true);
      try {
        for (const id of ids) {
          const item = sourceItems.find((i) => i.id === id);
          if (!item) continue;
          updateItem(id, { exit_status: 'archived' as ItemExitStatus });
        }
        setSelectedZombieIds(new Set());
        setSelectedOverdueIds(new Set());
      } finally {
        setBulkLoading(false);
      }
    },
    [updateItem]
  );

  const bulkDefer = useCallback(
    async (ids: Set<string>, sourceItems: Item[], reviewDate: Date) => {
      if (ids.size === 0) return;
      setBulkLoading(true);
      try {
        for (const id of ids) {
          const item = sourceItems.find((i) => i.id === id);
          if (!item) continue;
          updateItem(id, {
            exit_status: 'deferred' as ItemExitStatus,
            deferReviewDate: reviewDate,
          });
        }
        setSelectedZombieIds(new Set());
        setSelectedOverdueIds(new Set());
      } finally {
        setBulkLoading(false);
      }
    },
    [updateItem]
  );

  // ── Bulk defer date state ────────────────────────────────────────────────────
  const [bulkDeferDate, setBulkDeferDate] = useState(todayPlusDays(30));
  const [showBulkDeferInput, setShowBulkDeferInput] = useState<'zombie' | 'overdue' | null>(null);

  // ── Select all helpers ───────────────────────────────────────────────────────
  function toggleAllZombies() {
    if (selectedZombieIds.size === zombieItems.length) {
      setSelectedZombieIds(new Set());
    } else {
      setSelectedZombieIds(new Set(zombieItems.map((i) => i.id)));
    }
  }

  function toggleAllOverdue() {
    if (selectedOverdueIds.size === overdueDeferrals.length) {
      setSelectedOverdueIds(new Set());
    } else {
      setSelectedOverdueIds(new Set(overdueDeferrals.map((i) => i.id)));
    }
  }

  const handleGenerateNarrative = async () => {
    setIsNarrating(true);
    setHealthNarrative(null);
    const categoryBreakdown: Record<string, number> = {};
    for (const item of [...zombieItems, ...overdueDeferrals]) {
      if (item.itemCategory) {
        categoryBreakdown[item.itemCategory] = (categoryBreakdown[item.itemCategory] ?? 0) + 1;
      }
    }
    const result = await generateBacklogHealthNarrative({
      totalActiveItems: items.filter((i) => (i.exit_status ?? 'active') === 'active').length,
      zombieCount: zombieItems.length,
      overdueCount: overdueDeferrals.length,
      stalenessThresholdDays: stalenessThreshold,
      zombieSamples: zombieItems.slice(0, 12).map((z) => ({
        title: z.title,
        daysSinceUpdate: z.daysSinceUpdate,
        category: z.itemCategory,
        cycleCount: z.cycleIds.length,
      })),
      overdueSamples: overdueDeferrals.slice(0, 12).map((o) => ({
        title: o.title,
        daysOverdue: o.daysOverdue,
        category: o.itemCategory,
      })),
      categoryBreakdown,
    });
    setHealthNarrative(result);
    setIsNarrating(false);
  };

  // ── Empty state ──────────────────────────────────────────────────────────────
  const totalIssues = zombieItems.length + overdueDeferrals.length;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Skull className="h-4 w-4 text-[var(--text-sec)]" />}
        iconBg="bg-[var(--hover-tint)]"
        title="Backlog Health"
        subtitle="Identify and prune zombie priorities — stale active items and overdue deferrals that are quietly consuming space without moving forward."
      />

      {/* ── Summary + Controls ─────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          {totalIssues === 0 ? (
            <span className="flex items-center gap-1.5 text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)]">
              <CheckCircle className="h-4 w-4" /> Backlog is healthy
            </span>
          ) : (
            <span className="flex items-center gap-1.5 text-sm font-medium text-[var(--text-sec)]">
              <AlertTriangle className="h-4 w-4 text-[var(--mustard)]" />
              {totalIssues} item{totalIssues !== 1 ? 's' : ''} need attention
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* AI Health Narrative */}
          {totalIssues > 0 && backlogHealthNarratorEnabled && (
            <button
              onClick={handleGenerateNarrative}
              disabled={isNarrating}
              className="flex items-center gap-1.5 border border-[var(--slate)] bg-[var(--slate-dim)] px-3 py-1.5 text-xs text-[var(--text-sec)] hover:bg-[var(--slate-dim)] disabled:opacity-60 transition-colors"
              title="Ask AI to interpret these backlog health patterns"
            >
              <Sparkles className="h-3.5 w-3.5" />
              {isNarrating ? 'Analyzing…' : 'AI Diagnosis'}
            </button>
          )}

          {/* Filters toggle */}
          <button
            onClick={() => setShowFilters((v) => !v)}
            className="flex items-center gap-1.5 border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-1.5 text-xs text-[var(--text-sec)] hover:bg-[var(--surface)]"
          >
            <Filter className="h-3.5 w-3.5" />
            Filters
            {showFilters ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>

      {/* ── Filter bar ────────────────────────────────────────────────────────── */}
      {showFilters && (
        <div className="flex flex-wrap items-center gap-4 border border-[var(--rule)] bg-[var(--surface)] p-4">
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-[var(--text-sec)]">Staleness threshold</label>
            <div className="flex border border-[var(--rule)] bg-[var(--bg-raised)] overflow-hidden">
              {([30, 60, 90, 180] as StalenessThreshold[]).map((t) => (
                <button
                  key={t}
                  onClick={() => setStalenessThreshold(t)}
                  className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                    stalenessThreshold === t
                      ? 'bg-primary-600 text-white'
                      : 'text-[var(--text-sec)] hover:bg-[var(--surface)]'
                  }`}
                >
                  {t}d
                </button>
              ))}
            </div>
          </div>

          {categoryOptions.length > 0 && (
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-[var(--text-sec)]">Category</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="rounded border border-[var(--rule)] bg-[var(--bg-raised)] px-2 py-1 text-xs text-[var(--text-sec)] focus:border-primary-500 focus:outline-none"
              >
                <option value="all">All categories</option>
                {categoryOptions.map((c) => (
                  <option key={c} value={c}>
                    {c.charAt(0).toUpperCase() + c.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {keptActiveIds.size > 0 && (
            <button
              onClick={() => setKeptActiveIds(new Set())}
              className="flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]"
            >
              <RefreshCw className="h-3 w-3" />
              Reset {keptActiveIds.size} dismissed
            </button>
          )}
        </div>
      )}

      {/* ── AI Health Narrative Panel ──────────────────────────────────────────── */}
      {healthNarrative && (
        <div className="border border-[var(--slate)] bg-[var(--slate-dim)] p-5">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
              <h3 className="text-sm font-semibold text-[var(--text-sec)]">AI Health Diagnosis</h3>
            </div>
            <button
              onClick={() => setHealthNarrative(null)}
              className="p-0.5 hover:bg-[var(--slate-dim)] rounded"
            >
              <X className="h-3.5 w-3.5 text-[var(--text-ter)]" />
            </button>
          </div>
          <p className="text-sm font-medium text-[var(--text-sec)] mb-2">{healthNarrative.headline}</p>
          <p className="text-sm text-[var(--text-sec)] mb-4">{healthNarrative.assessment}</p>
          {healthNarrative.patterns.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-[var(--text-sec)] mb-1.5 uppercase tracking-wide">Patterns</p>
              <ul className="space-y-1">
                {healthNarrative.patterns.map((pattern, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-[var(--text-sec)]">
                    <span className="text-[var(--text-sec)] mt-0.5">•</span>
                    {pattern}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {healthNarrative.topActions.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-[var(--text-sec)] mb-1.5 uppercase tracking-wide">Recommended Actions</p>
              <ul className="space-y-1">
                {healthNarrative.topActions.map((action, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-[var(--text-sec)]">
                    <span className="text-[var(--text-sec)] font-bold mt-0.5">{i + 1}.</span>
                    {action}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {healthNarrative.prognosis && (
            <p className="text-xs text-[var(--text-sec)] italic border-t border-[var(--slate)] pt-3 mt-2">
              {healthNarrative.prognosis}
            </p>
          )}
          <p className="text-xs text-[var(--text-sec)] mt-2 text-right">Advisory only — all decisions are yours.</p>
        </div>
      )}

      {/* ── Empty state ────────────────────────────────────────────────────────── */}
      {totalIssues === 0 && (
        <div className="border border-[var(--green)] bg-[var(--green-dim)] p-8 text-center">
          <CheckCircle className="mx-auto h-10 w-10 text-[var(--green)]" />
          <h3 className="mt-3 text-base font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">No zombie priorities found</h3>
          <p className="mt-1 text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
            All active items have been touched in the last {stalenessThreshold} days, and no deferrals are overdue.
            {keptActiveIds.size > 0 && (
              <> ({keptActiveIds.size} item{keptActiveIds.size !== 1 ? 's' : ''} dismissed this session.)</>
            )}
          </p>
        </div>
      )}

      {/* ── Zombie Priorities Section ─────────────────────────────────────────── */}
      {zombieItems.length > 0 && (
        <section>
          <div
            className="flex cursor-pointer items-center justify-between border border-[var(--rule)] bg-[var(--surface)] px-4 py-3"
            onClick={() => setZombieCollapsed((v) => !v)}
          >
            <div className="flex items-center gap-2">
              <Skull className="h-4 w-4 text-[var(--text-ter)]" />
              <h2 className="text-sm font-semibold text-[var(--text-pri)]">
                Zombie Priorities
                <span className="ml-2 rounded-full bg-[var(--hover-tint)] px-2 py-0.5 text-xs font-normal text-[var(--text-sec)]">
                  {zombieItems.length}
                </span>
              </h2>
            </div>
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              {selectedZombieIds.size > 0 && (
                <div className="flex items-center gap-2">
                  {showBulkDeferInput === 'zombie' ? (
                    <div className="flex items-center gap-1.5">
                      <input
                        type="date"
                        min={todayPlusDays(1)}
                        value={bulkDeferDate}
                        onChange={(e) => setBulkDeferDate(e.target.value)}
                        className="rounded border border-[var(--rule)] px-2 py-1 text-xs"
                      />
                      <button
                        onClick={() => {
                          bulkDefer(selectedZombieIds, zombieItems, new Date(bulkDeferDate));
                          setShowBulkDeferInput(null);
                        }}
                        disabled={bulkLoading}
                        className="rounded bg-[var(--mustard)] px-2 py-1 text-xs font-medium text-white hover:bg-[var(--mustard)] disabled:opacity-40"
                      >
                        Defer {selectedZombieIds.size}
                      </button>
                      <button onClick={() => setShowBulkDeferInput(null)} className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]">Cancel</button>
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => setShowBulkDeferInput('zombie')}
                        disabled={bulkLoading}
                        className="rounded border border-[var(--mustard)] px-2.5 py-1 text-xs text-[var(--mustard)] hover:bg-[var(--mustard-dim)] disabled:opacity-40"
                      >
                        <Clock className="mr-1 inline h-3 w-3" />
                        Defer {selectedZombieIds.size}
                      </button>
                      <button
                        onClick={() => bulkArchive(selectedZombieIds, zombieItems)}
                        disabled={bulkLoading}
                        className="rounded border border-[var(--coral)] px-2.5 py-1 text-xs text-[var(--coral)] hover:bg-[var(--coral-dim)] disabled:opacity-40"
                      >
                        {bulkLoading ? <Loader2 className="inline h-3 w-3 animate-spin" /> : <Archive className="mr-1 inline h-3 w-3" />}
                        Archive {selectedZombieIds.size}
                      </button>
                    </>
                  )}
                </div>
              )}
              {zombieCollapsed ? (
                <ChevronDown className="h-4 w-4 text-[var(--text-ter)]" />
              ) : (
                <ChevronUp className="h-4 w-4 text-[var(--text-ter)]" />
              )}
            </div>
          </div>

          {!zombieCollapsed && (
            <div className="border border-t-0 border-[var(--rule)] bg-[var(--bg-raised)]">
              {/* Select all */}
              <div className="flex items-center gap-2 border-b border-[var(--rule)] px-4 py-2">
                <input
                  type="checkbox"
                  checked={selectedZombieIds.size === zombieItems.length && zombieItems.length > 0}
                  onChange={toggleAllZombies}
                  className="h-4 w-4 rounded border-[var(--rule)] text-primary-600"
                />
                <span className="text-xs text-[var(--text-ter)]">
                  {selectedZombieIds.size > 0
                    ? `${selectedZombieIds.size} of ${zombieItems.length} selected`
                    : `Select all ${zombieItems.length}`}
                </span>
                <span className="ml-auto text-xs text-[var(--text-ter)]">
                  No updates in {stalenessThreshold}+ days · not cycling forward
                </span>
              </div>
              <div className="divide-y divide-[var(--rule)] p-3 space-y-2">
                {zombieItems.map((item) => (
                  <ZombieRow
                    key={item.id}
                    item={item}
                    selected={selectedZombieIds.has(item.id)}
                    onToggle={() =>
                      setSelectedZombieIds((s) => {
                        const n = new Set(s);
                        if (n.has(item.id)) { n.delete(item.id); } else { n.add(item.id); }
                        return n;
                      })
                    }
                    onArchive={() => archiveItem(item)}
                    onDefer={(d) => deferItem(item, d)}
                    onKeepActive={() => keepActive(item)}
                    inlineDefer={inlineDefer}
                    setInlineDefer={setInlineDefer}
                    loading={loadingIds.has(item.id)}
                  />
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* ── Overdue Deferrals Section ─────────────────────────────────────────── */}
      {overdueDeferrals.length > 0 && (
        <section>
          <div
            className="flex cursor-pointer items-center justify-between border border-[var(--mustard)] bg-[var(--mustard-dim)] px-4 py-3"
            onClick={() => setOverdueCollapsed((v) => !v)}
          >
            <div className="flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-[var(--mustard)]" />
              <h2 className="text-sm font-semibold text-[var(--text-pri)]">
                Overdue Deferrals
                <span className="ml-2 rounded-full bg-[var(--mustard-dim)] px-2 py-0.5 text-xs font-normal text-[var(--mustard)]">
                  {overdueDeferrals.length}
                </span>
              </h2>
            </div>
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              {selectedOverdueIds.size > 0 && (
                <div className="flex items-center gap-2">
                  {showBulkDeferInput === 'overdue' ? (
                    <div className="flex items-center gap-1.5">
                      <input
                        type="date"
                        min={todayPlusDays(1)}
                        value={bulkDeferDate}
                        onChange={(e) => setBulkDeferDate(e.target.value)}
                        className="rounded border border-[var(--rule)] px-2 py-1 text-xs"
                      />
                      <button
                        onClick={() => {
                          bulkDefer(selectedOverdueIds, overdueDeferrals, new Date(bulkDeferDate));
                          setShowBulkDeferInput(null);
                        }}
                        disabled={bulkLoading}
                        className="rounded bg-[var(--mustard)] px-2 py-1 text-xs font-medium text-white hover:bg-[var(--mustard)] disabled:opacity-40"
                      >
                        Re-defer {selectedOverdueIds.size}
                      </button>
                      <button onClick={() => setShowBulkDeferInput(null)} className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]">Cancel</button>
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => setShowBulkDeferInput('overdue')}
                        disabled={bulkLoading}
                        className="rounded border border-[var(--mustard)] px-2.5 py-1 text-xs text-[var(--mustard)] hover:bg-[var(--mustard-dim)] disabled:opacity-40"
                      >
                        <Undo2 className="mr-1 inline h-3 w-3" />
                        Re-defer {selectedOverdueIds.size}
                      </button>
                      <button
                        onClick={() => bulkArchive(selectedOverdueIds, overdueDeferrals)}
                        disabled={bulkLoading}
                        className="rounded border border-[var(--coral)] px-2.5 py-1 text-xs text-[var(--coral)] hover:bg-[var(--coral-dim)] disabled:opacity-40"
                      >
                        {bulkLoading ? <Loader2 className="inline h-3 w-3 animate-spin" /> : <Archive className="mr-1 inline h-3 w-3" />}
                        Archive {selectedOverdueIds.size}
                      </button>
                    </>
                  )}
                </div>
              )}
              {overdueCollapsed ? (
                <ChevronDown className="h-4 w-4 text-[var(--text-ter)]" />
              ) : (
                <ChevronUp className="h-4 w-4 text-[var(--text-ter)]" />
              )}
            </div>
          </div>

          {!overdueCollapsed && (
            <div className="border border-t-0 border-[var(--mustard)] bg-[var(--bg-raised)]">
              <div className="flex items-center gap-2 border-b border-[var(--rule)] px-4 py-2">
                <input
                  type="checkbox"
                  checked={selectedOverdueIds.size === overdueDeferrals.length && overdueDeferrals.length > 0}
                  onChange={toggleAllOverdue}
                  className="h-4 w-4 rounded border-[var(--rule)] text-primary-600"
                />
                <span className="text-xs text-[var(--text-ter)]">
                  {selectedOverdueIds.size > 0
                    ? `${selectedOverdueIds.size} of ${overdueDeferrals.length} selected`
                    : `Select all ${overdueDeferrals.length}`}
                </span>
                <span className="ml-auto text-xs text-[var(--text-ter)]">
                  Review date has passed — decide: archive, re-defer, or reactivate
                </span>
              </div>
              <div className="divide-y divide-[var(--rule)] p-3 space-y-2">
                {overdueDeferrals.map((item) => (
                  <OverdueDeferralRow
                    key={item.id}
                    item={item}
                    selected={selectedOverdueIds.has(item.id)}
                    onToggle={() =>
                      setSelectedOverdueIds((s) => {
                        const n = new Set(s);
                        if (n.has(item.id)) { n.delete(item.id); } else { n.add(item.id); }
                        return n;
                      })
                    }
                    onArchive={() => archiveItem(item)}
                    onRedefer={(d) => deferItem(item, d)}
                    onReactivate={() => reactivateItem(item)}
                    inlineDefer={inlineDefer}
                    setInlineDefer={setInlineDefer}
                    loading={loadingIds.has(item.id)}
                  />
                ))}
              </div>
            </div>
          )}
        </section>
      )}

    </div>
  );
}
